import React, { MouseEventHandler, useEffect, useRef } from "react";
import classNames from "classnames";

import styles from "./modalRegular.module.scss";
import Icon from "@/ui/icon";

type StandardModalHeaderProps = {
    caption: string;
    captionAddon?: string;
    onHide: () => void;
    hideBorder?: boolean;
};

export const StandardModalHeader = ({
    onHide = () => {},
    caption,
    captionAddon,
    hideBorder = false,
}: StandardModalHeaderProps) => (
    <>
        <div
            className={classNames(
                styles.modalHeader,
                {
                    [styles.noBorder]: hideBorder,
                },
                "standard-reach-header",
            )}
        >
            <div className={styles.title}>
                {caption}
                {captionAddon && <small className={styles.titleAddon}>{captionAddon}</small>}
            </div>
            <button type="button" className={styles.btnClose} onClick={onHide}>
                <Icon icon="fermer" />
            </button>
        </div>
    </>
);

type ModalRegularProps = {
    children: React.ReactNode;
    headerCaption?: string;
    headerCaptionAddon?: string;
    isOpen: boolean;
    onHide: () => void;
    hideBorder?: boolean;
    isFullScreen?: boolean;
    hideHeader?: boolean;
};

const ModalRegular = ({
    children,
    headerCaption = "",
    headerCaptionAddon,
    isOpen = false,
    onHide = () => {},
    hideBorder = false,
    isFullScreen = false,
    hideHeader = false,
}: ModalRegularProps) => {
    const dialogRef = useRef<HTMLDialogElement>(null);

    const handleFocus: MouseEventHandler<HTMLElement> = (e) => {
        if (isOpen && dialogRef.current === e.target) {
            onHide();
        }
    };

    useEffect(() => {
        if (isOpen && dialogRef.current) {
            dialogRef.current?.showModal();
        } else {
            dialogRef.current?.close();
        }
    }, [isOpen]);

    return (
        <dialog className={styles.component} ref={dialogRef} onClick={handleFocus}>
            <div
                className={classNames(styles.dialogContent, {
                    [styles.isFullScreen]: isFullScreen,
                })}
                aria-label={headerCaption}
            >
                <div>
                    {!hideHeader && (
                        <StandardModalHeader
                            caption={headerCaption}
                            captionAddon={headerCaptionAddon}
                            onHide={onHide}
                            hideBorder={hideBorder}
                        />
                    )}
                    {children}
                </div>
            </div>
        </dialog>
    );
};

export default ModalRegular;
